// Each method/amount selection keeps its own operation through timeouts and
// reloads. Other selections are independent checkouts, never retries of it.
import api from './api';

const memory = new Map();
const keyFor = (tenant, user) => `serviceemp:simpay:${tenant}:${user}`;
const checkoutKey = (tenant, user, method, amount) => `${keyFor(tenant, user)}:checkout:${method}:${Math.round(Number(amount) * 100)}`;
const read = key => {
  if (memory.has(key)) return memory.get(key);
  try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; }
};
const saveAt = (key, operation) => {
  memory.set(key, operation);
  try { sessionStorage.setItem(key, JSON.stringify(operation)); return true; } catch { return false; }
};
const removeAt = key => {
  memory.delete(key);
  try { sessionStorage.removeItem(key); } catch { /* optional storage */ }
};

function migrateLegacy(tenantId, userId) {
  const oldKey = keyFor(tenantId, userId), previous = read(oldKey);
  if (!previous) return;
  if (!previous.id || !previous.payload?.method || !(Number(previous.payload.amount) > 0)) {
    throw new Error('Saved payment needs manual review. Please contact support.');
  }
  const key = checkoutKey(tenantId, userId, previous.payload.method, previous.payload.amount);
  const current = read(key);
  if (!current && saveAt(key, previous)) removeAt(oldKey);
  else if (current?.id === previous.id) removeAt(oldKey);
}

export const paymentSelectionMatches = (data, selection) => (
  data?.method === selection.method
  && Number.isFinite(Number(data?.charged_amount))
  && Math.round(Number(data.charged_amount) * 100) === Math.round(Number(selection.amount) * 100)
);

export function finishSimpayOperation(tenantId, userId, orderId) {
  const prefix = keyFor(tenantId, userId);
  const keys = new Set(memory.keys());
  try { for (let i = 0; i < sessionStorage.length; i++) keys.add(sessionStorage.key(i)); } catch { /* optional storage */ }
  for (const key of keys) {
    if ((key === prefix || key?.startsWith(prefix + ':checkout:')) && read(key)?.orderId === orderId) removeAt(key);
  }
}

export async function submitSimpayCheckout({ userId, tenantId, amount, method }, client = api) {
  if (!userId || !tenantId) throw new Error('Sign in before creating a payment');
  if (!method || !Number.isFinite(Number(amount)) || Number(amount) <= 0) throw new Error('Select a valid payment amount');
  migrateLegacy(tenantId, userId);
  const key = checkoutKey(tenantId, userId, method, amount);
  let operation = read(key);
  if (operation && (!operation.id || !operation.payload || typeof operation.payload.method !== 'string')) {
    throw new Error('Saved payment needs manual review. Please contact support.');
  }
  const resumed = Boolean(operation);
  if (!operation) {
    operation = { id: crypto.randomUUID(), payload: { amount, method, currency: 'USD' } };
  }
  const save = () => saveAt(key, operation);
  const clear = () => removeAt(key);
  save(); // Before networking: a timeout must not lose the operation identity.
  try {
    const res = operation.orderId
      ? await client.get(`/api/simpay/payins/${encodeURIComponent(operation.orderId)}`)
      : await client.post('/api/gateway/deposit', operation.payload, { headers: { 'X-Idempotency-Key': operation.id } });
    if (res.data?.order_id) {
      operation.orderId = res.data.order_id;
      save();
    }
    if (['APPROVED', 'FAILED'].includes(res.data?.status) && !res.data?.manual_review) clear();
    const selectionConflict = Boolean(res.data?.selection_conflict)
      || !paymentSelectionMatches(res.data, { method, amount });
    return { ...res, data: { ...res.data, resumed_operation: resumed,
      selection_conflict: selectionConflict,
      ...(selectionConflict ? { pay_url: null } : {}),
    } };
  } catch (error) {
    // Definitive validation failure on a brand-new attempt is safe to correct.
    if (!resumed && [400, 403, 422].includes(error.response?.status)) { clear(); throw error; }
    return { data: { provider: 'simpay', pending: true, status: 'PENDING_GATEWAY',
      method: operation.payload.method, charged_amount: operation.payload.amount,
      resumed_operation: resumed,
      selection_conflict: !paymentSelectionMatches({ method: operation.payload.method, charged_amount: operation.payload.amount }, { method, amount }),
      order_id: operation.orderId, message: 'This payment remains pending and tracked. Only a verified successful payment credits your wallet.' } };
  }
}
